<template>
  <main>
    <div class="container">
      <div class="row">
        <div class="col-full">
          <h1 class="heading-xl">
            {{ $t('language.heading') }}
          </h1>
          <al-list card>
            <al-list-item
              wrap
              cta-icon=""
              name="country-and-language"
              data-test="country-and-language-item">
              <div class="row margin-left-none">
                <div class="col-full">
                  <p class="margin-bottom-none">
                    {{ countryName }}
                    <al-flag :country-code="countryCode" />
                  </p>
                  <p>
                    {{ languageName }}
                  </p>
                </div>
              </div>
              <a
                v-if="hasMultipleLanguages"
                slot="cta"
                v-toggle:al-action-sheet="'language-settings-action-sheet'"
                tabindex="0"
                class="language-edit"
                data-test="language-edit">
                {{ $t('language.edit') }}
              </a>
            </al-list-item>
          </al-list>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <p class="legal" data-test="languageSettings-legal">
            {{ $t('common.countryLegalNotice') }}
          </p>
        </div>
      </div>
      <div class="row-vertical-align-bottom">
        <div class="col-full align-center">
          <back-button
            v-analytics:click="$EVENTS.GO_BACK_BUTTON"
            data-test="languageSettings-backButton"
            :location="backButtonRoute">
            {{ $t('button.goBack') }}
          </back-button>
        </div>
      </div>
      <tux-locale-picker
        v-model="appLocale"
        language-only
        name="language-settings-action-sheet"
        @open="handleLocalePickerOpen"
        @confirm="handleLocalePickerConfirm" />
    </div>
  </main>
</template>

<script>
import { AlFlag, AlList, AlListItem } from '@mc-alberta/alcapone'
import { TuxLocalePicker } from '@mc-alberta/tuxedo'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import { ROUTES } from '@/constants'
import * as types from '@/store/mutation-types.js'

export default {
  name: ROUTES.LANGUAGE,
  components: { AlFlag, AlList, AlListItem, BackButton, TuxLocalePicker },
  data() {
    return {
      backButtonRoute: { name: ROUTES.SETTINGS }
    }
  },
  computed: {
    ...mapState({
      countryCode: (state) => state.user.consumer.countryCode
    }),
    ...mapGetters(['locale', 'serviceErrorResponse']),
    countryName() {
      return this.$i18nData.getCountryName(this.locale, this.locale)
    },
    languageName() {
      return this.$i18nData.getLanguageName(this.locale, this.locale)
    },
    hasMultipleLanguages() {
      const languageConfig = this.$i18nData.getCountryConfig(this.countryCode)
      return languageConfig?.supportedLanguages.length > 1
    },
    appLocale: {
      get() {
        return this.locale
      },
      set(newLocale) {
        this.handleLocaleUpdate(newLocale)
      }
    }
  },
  beforeDestroy() {
    this.clearServiceLayerError()
  },
  methods: {
    ...mapActions(['clearServiceLayerError', 'setLocale', 'setPreviousLocale', 'updateLanguage']),
    ...mapMutations({
      setLocaleSelectorSelected: types.SET_LOCALE_SELECTOR_SELECTED
    }),
    handleLocalePickerOpen({ currentLocale }) {
      if (!currentLocale) return

      this.setPreviousLocale(currentLocale)
      this.setLocaleSelectorSelected(true)
    },
    async handleLocalePickerConfirm(confirmedLocale) {
      if (!confirmedLocale) return

      const { languageCode } = this.$i18nData.getLocaleParts(confirmedLocale) || {}

      await this.updateLanguage(languageCode)

      if (this.serviceErrorResponse()) {
        return this.$router.replace({ name: ROUTES.ERROR })
      }

      this.setLocale(confirmedLocale)

      this.trackLocaleChanges(this.$EVENTS.CHANGE_LANGUAGE_CONFIRM)
    },
    handleLocaleUpdate(confirmedLocale) {
      if (!confirmedLocale) return

      this.setLocale(confirmedLocale)
    }
  }
}
</script>

<style lang="scss" scoped>
.language-edit {
  width: max-content;
}
</style>
